<template>
  <div class="settings-org-section">
    <SettingsSectionHeader
      title="Account"
      subtitle="Your profile and access"
      icon="pi-user"
    />

    <div class="settings-org-section__body">
      <template v-if="user.isLogged">
        <section
          class="settings-account-section__block"
          aria-labelledby="settings-account-profile-heading"
        >
          <h3
            id="settings-account-profile-heading"
            class="settings-account-section__heading"
          >
            Profile
          </h3>
          <div class="settings-account-section__grid">
            <div class="settings-account-section__field">
              <span class="settings-account-section__label">Name</span>
              <p class="settings-account-section__value">{{ displayName }}</p>
            </div>
            <div class="settings-account-section__field">
              <span class="settings-account-section__label">Account email</span>
              <p class="settings-account-section__value settings-account-section__value--break">
                {{ user.email || '—' }}
              </p>
            </div>
            <div class="settings-account-section__field">
              <span class="settings-account-section__label">Role</span>
              <p class="settings-account-section__value">{{ roleLabel || '—' }}</p>
            </div>
          </div>
        </section>
      </template>

      <div v-else class="settings-org-section__empty">
        <i class="pi pi-user settings-org-section__empty-icon" aria-hidden="true" />
        <p>Sign in to view your account.</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import SettingsSectionHeader from '../SettingsSectionHeader.vue';
import {
  formatUserDisplayName,
  resolvePrimaryRoleLabel
} from '@/features/drivers-workspace/utils/sidebarUserUtils';

defineOptions({ name: 'SettingsAccountSection' });

const store = useStore();
const user = computed(() => store.state.users?.user || {});

const displayName = computed(() =>
  formatUserDisplayName(user.value.name, user.value.email)
);

const roleLabel = computed(() => resolvePrimaryRoleLabel(user.value.roles));
</script>

<style scoped>
.settings-account-section__block {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.settings-account-section__heading {
  margin: 0;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--crc-text-muted, var(--text-color-secondary));
}

.settings-account-section__grid {
  display: grid;
  grid-template-columns: repeat(1, minmax(0, 1fr));
  gap: 0.75rem;
}

@media (min-width: 640px) {
  .settings-account-section__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.settings-account-section__field {
  border: 1px solid var(--crc-panel-border, var(--surface-border));
  border-radius: 0.75rem;
  padding: 0.875rem 1rem;
  background: var(--crc-bar-bg, var(--surface-card));
}

.settings-account-section__label {
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--crc-text-muted, var(--text-color-secondary));
}

.settings-account-section__value {
  margin: 0.375rem 0 0;
  font-size: 0.9375rem;
  color: var(--crc-text, var(--text-color));
  word-break: break-word;
}

.settings-account-section__value--break {
  overflow-wrap: anywhere;
}
</style>
